/**
 * Emboss — directional gradient producing a relief-like image.
 *
 *     out = 0.5 + amount · directionalDerivative(I, θ)
 *
 * Each pixel's output is the projection of the local image gradient
 * onto the user-supplied direction θ, offset by 0.5 so flat regions
 * sit at mid-grey. The result looks like the image was carved into a
 * neutral surface and lit from direction θ — classic image-effect.
 *
 * Modes:
 *   • 'gradient' — only the directional derivative is written; original
 *                  colour is discarded. The "bumpy grey" look.
 *   • 'flat'     — original luminance retained; only the chroma is
 *                  replaced by the gradient. Preserves the silhouette
 *                  of the original while overlaying the emboss texture.
 *
 * Pipeline slot: destructive pre-kernel commit. Identity short-circuit:
 * amount ≤ 0 → no-op.
 *
 * References:
 *   • Standard image-processing textbook (Gonzalez & Woods §3.6.1
 *     directional derivative kernels).
 *
 * License: BLRRD is MIT. NO GPLv3 source consulted.
 *
 * @module src/pipeline/filters/spatial/emboss
 */

import { mirrorIndex, validateRGBA, LUMA_R, LUMA_G, LUMA_B } from './_util.js';

/** Slider ranges. */
export const EMBOSS_AMOUNT_MIN = 0;
export const EMBOSS_AMOUNT_MAX = 2;
export const EMBOSS_ANGLE_MIN  = 0;
export const EMBOSS_ANGLE_MAX  = 360;

const IDENTITY_EPS = 1e-6;

/**
 * Default Emboss parameters. Identity (amount = 0).
 *
 * @returns {{ angleDeg: number, amount: number, mode: 'gradient'|'flat' }}
 */
export function defaultParams() {
    return { angleDeg: 45, amount: 0, mode: 'gradient' };
}

/**
 * Identity test.
 *
 * @param {{ amount?: number }} params
 * @returns {boolean}
 */
export function isIdentityEmboss(params) {
    if (!params) return true;
    const a = +params.amount;
    if (!Number.isFinite(a)) return true;
    return a <= IDENTITY_EPS;
}

/**
 * Apply Emboss in-place on a packed RGBA Float32 buffer.
 *
 * Algorithm (per pixel, integer (x, y)):
 *   gx = (I[x+1,y]   - I[x-1,y])   / 2
 *   gy = (I[x,  y+1] - I[x,  y-1]) / 2
 *   g  = gx·cos(θ) + gy·sin(θ)
 *   out = mode == 'gradient' ? 0.5 + amount·g
 *                            : original_luma + amount·g          ('flat')
 *
 * `I` is the per-channel value (we run the same gradient calc on R,
 * G, B independently — this preserves any colour-aware structure
 * already in the source).
 *
 * Boundary: central-difference at the edge wraps via mirrorIndex.
 *
 * @param {Float32Array} rgba    scene-linear RGBA, length 4·w·h
 * @param {number} w
 * @param {number} h
 * @param {object} params
 * @param {number} params.angleDeg
 * @param {number} params.amount
 * @param {'gradient'|'flat'} params.mode
 * @returns {Float32Array}       same `rgba` reference (in-place)
 */
export function applyEmboss(rgba, w, h, params) {
    validateRGBA('applyEmboss', rgba, w, h);
    const p = params || {};
    if (isIdentityEmboss(p)) return rgba;

    const angleDeg = +p.angleDeg || 0;
    const amount   = Math.min(EMBOSS_AMOUNT_MAX, Math.max(EMBOSS_AMOUNT_MIN, +p.amount));
    const mode     = p.mode === 'flat' ? 'flat' : 'gradient';
    const rad      = (angleDeg * Math.PI) / 180;
    const cx       = Math.cos(rad);
    const cy       = Math.sin(rad);

    const src = new Float32Array(rgba);

    for (let y = 0; y < h; y++) {
        const yU = mirrorIndex(y - 1, h);
        const yD = mirrorIndex(y + 1, h);
        for (let x = 0; x < w; x++) {
            const xL = mirrorIndex(x - 1, w);
            const xR = mirrorIndex(x + 1, w);
            const jC = (y  * w + x ) * 4;
            const jL = (y  * w + xL) * 4;
            const jR = (y  * w + xR) * 4;
            const jU = (yU * w + x ) * 4;
            const jD = (yD * w + x ) * 4;
            for (let c = 0; c < 3; c++) {
                const gx = (src[jR + c] - src[jL + c]) * 0.5;
                const gy = (src[jD + c] - src[jU + c]) * 0.5;
                const g  = gx * cx + gy * cy;
                if (mode === 'gradient') {
                    rgba[jC + c] = 0.5 + amount * g;
                } else {
                    /* 'flat' — preserve per-channel luminance, add gradient. */
                    rgba[jC + c] = src[jC + c] + amount * g;
                }
            }
            /* alpha untouched. */
        }
    }

    /* If mode='gradient' AND user wants a monochrome emboss, the user
     * is expected to follow up with a desaturate. We deliberately
     * don't force a luma-projection here so the colour-aware embossing
     * (different per channel) stays available for stylistic use. The
     * `mode === 'gradient'` output already collapses to ~mid-grey for
     * flat colour, and the chroma comes from genuine cross-channel
     * gradient differences. */
    /* Suppress unused-variable warning for LUMA constants — they're
     * imported for potential future luma-projection mode. */
    void LUMA_R; void LUMA_G; void LUMA_B;

    return rgba;
}
